export interface GeoGridExample {
  /** Broad search category monitored, not an exact keyword claim. */
  searchCategory: string;
  /** Genuine GeoGrid scan image. */
  image: string;
  /** Broad Australian region only — never an exact suburb. */
  region?: string;
  /** Cautious summary of the movement observed in the monitored area. */
  observation: string;
}

/**
 * A single scannable result tile. Values must be directional unless a figure is
 * genuinely verified in the client's own reports and approved for publication.
 */
export interface CaseStudyMetric {
  /** Short headline value, e.g. "Wider" or a verified figure. */
  value: string;
  /** What the value refers to. */
  label: string;
  /** Genuine measurement period / scope note. Shown in small text. */
  note: string;
}

export interface CaseStudy {
  slug: string;
  /** Anonymous business type — no names, logos, suburbs or cities. */
  business: string;
  service: string;
  headline: string;
  /** Short plain-language overview of the commercial problem. No figures. */
  summary: string;
  /** Only stated where the period is genuinely known. */
  measurementPeriod?: string;
  /** 1-3 scannable result tiles. Never pad to three with invented figures. */
  metrics: CaseStudyMetric[];
  /** Non-identifying supporting image, where one genuinely exists. */
  image?: string;
  /** Alt text for the supporting image. */
  imageAlt?: string;
  geoGrid?: GeoGridExample;
  startingPoint: string[];
  workCompleted: string[];
  /** Commercially meaningful, directional outcomes for the business owner. */
  whatImproved: string[];
  /** Why the improvement mattered — calls, enquiries, trust, follow-up. */
  whyItMattered: string;
  limitations: string[];
}


/** Standard context statement shown on every case study. */
export const caseStudyContext =
  "Results vary based on location, competition, customer demand, seasonality and how enquiries are handled. These examples show outcomes observed for individual businesses and are not guarantees of future performance.";

/**
 * Client work is kept anonymous where permission to publish identifying
 * information has not been provided. Outcomes are directional and based only on
 * the evidence available — no exact rankings, review counts, lead totals,
 * cost-per-lead, page-speed or dollar figures are published.
 */
export const caseStudies: CaseStudy[] = [
  {
    slug: "pest-control-local-search-foundation",
    business: "Australian Pest Control Business",
    service: "Google Business Profile & Local SEO",
    headline: "Getting Found Across More of the Service Area",
    summary:
      "The business was only being found in a small part of the area it serviced, so nearby customers were rarely seeing it when searching for pest control.",
    measurementPeriod: "Observed over several months",
    geoGrid: {
      searchCategory: "Pest control services",
      image: "/images/heatmap-painter.png",
      observation:
        "The business began appearing more prominently across a wider portion of the tracked service area, creating more opportunities to be found by nearby customers.",
    },
    metrics: [
      {
        value: "Improved",
        label: "Google Maps visibility across the monitored grid",
        note: "Average position movement for the tracked search term and monitored grid only — not an entire city. Recorded over several months.",
      },
      {
        value: "Upward",
        label: "Percentage change in profile actions",
        note: "Directional trend only. An exact percentage will be published once verified in the client's reports.",
      },
      {
        value: "More",
        label: "Calls and website visits from the profile",
        note: "Recorded during the measurement period; totals not published without verified client approval.",
      },
    ],
    startingPoint: [
      "The business was difficult to find outside a small part of the area it serviced.",
      "Service and category information was incomplete, so Google had little to match customer searches against.",
      "There was no tracking in place, so the owner could not see where enquiries were coming from.",
    ],
    workCompleted: [
      "Rebuilt the Google Business Profile around the services and areas the business actually wants work in.",
      "Introduced consistent profile activity and a simple review-request process after completed jobs.",
      "Set up visibility tracking for relevant pest control searches across the service area.",
    ],
    whatImproved: [
      "The business became more visible across additional parts of its tracked service area, giving nearby customers more opportunities to find the profile, visit the website and make contact.",
      "The profile recorded an upward trend in customer actions such as calls and website visits during the measurement period.",
      "The owner could finally see which parts of the service area the business was showing up in.",
    ],
    whyItMattered:
      "Pest control work is urgent and local — customers call whoever they see first. Appearing across more of the service area means more chances to be the business that gets the call rather than a competitor a few suburbs away.",
    limitations: [
      "Google Maps visibility varies by search term, location, competition and the position of the person searching.",
      "Monitored areas represent a selected scan, not rankings across an entire city.",
    ],
  },
  {
    slug: "roofing-managed-website",
    business: "Australian Roofing Business",
    service: "Managed Website",
    headline: "A Website That Makes It Easy to Ask for a Quote",
    summary:
      "Potential customers were reaching an outdated site that was hard to use on a phone, so many left without calling or requesting a quote.",
    measurementPeriod: "Observed over several months",
    image: "/images/roofing-work-residential.jpg",
    imageAlt: "Australian roofing contractor working on a residential roof",
    metrics: [
      {
        value: "Improved",
        label: "Visibility of the site's roofing service pages",
        note: "Relates to the tracked search terms monitored for this business only. Recorded over several months.",
      },
      {
        value: "Upward",
        label: "Percentage change in website conversions",
        note: "Directional trend only. An exact percentage will be published once verified in the client's reports.",
      },
      {
        value: "More",
        label: "Quote requests and calls from mobile",
        note: "Recorded during the measurement period; totals not published without verified client approval.",
      },
    ],
    startingPoint: [
      "Potential customers were reaching an outdated and confusing website.",
      "The roofing services on offer were bundled together, so visitors could not tell whether the business did the work they needed.",
      "There was no obvious way to call or request a quote from a phone.",
    ],
    workCompleted: [
      "Built a managed, mobile-first website with a clear page for each roofing service.",
      "Added prominent call buttons and a short quote-request form on every page.",
      "Took over ongoing hosting, updates and content changes so the site stays current.",
    ],
    whatImproved: [
      "The new website gave potential customers a clearer understanding of the roofing services and made it easier to call or request a quote from a mobile device.",
      "Mobile visitors no longer had to hunt for contact details — calling or requesting a quote is now one tap from anywhere on the site.",
      "The improved website recorded stronger enquiry activity during the measurement period.",
    ],
    whyItMattered:
      "Most roofing enquiries start on a phone, often after storm damage or a leak. Removing friction between landing on the site and asking for a quote gives the business a better chance of hearing from customers who would otherwise have moved on.",
    limitations: [
      "Website enquiry activity depends on demand, seasonality and the wider marketing activity driving visitors to the site.",
      "This is an ongoing managed website service, described here in general terms only.",
    ],
  },
  {
    slug: "plumbing-local-visibility",
    business: "Australian Plumbing Business",
    service: "Google Business Profile & Local SEO",
    headline: "Showing Up When Nearby Customers Search",
    summary:
      "The profile was live but largely inactive, so the business was missing from searches happening across much of the area it covered.",
    measurementPeriod: "Observed over several months",
    geoGrid: {
      searchCategory: "Plumbing services",
      image: "/images/heatmap-pool-builders.png",
      observation:
        "The business began appearing more prominently across a wider portion of the tracked service area, creating more opportunities to be found by nearby customers.",
    },
    metrics: [
      {
        value: "Improved",
        label: "Google Maps visibility across the monitored grid",
        note: "Average position movement for the tracked search term and monitored grid only — not an entire city. Recorded over several months.",
      },
      {
        value: "Upward",
        label: "Percentage change in profile actions",
        note: "Directional trend only. An exact percentage will be published once verified in the client's reports.",
      },
      {
        value: "More",
        label: "Calls and direction requests",
        note: "Recorded during the measurement period; totals not published without verified client approval.",
      },
    ],
    startingPoint: [
      "The business was only appearing for searches very close to its own location.",
      "Service and category details were thin, so relevant plumbing searches were harder for Google to match.",
      "Photos and updates had not been maintained, giving customers little reason to choose the profile.",
    ],
    workCompleted: [
      "Completed the profile with the specific plumbing services and categories the business wants work for.",
      "Established a consistent photo, update and review routine.",
      "Set up visibility tracking across the areas the business services.",
    ],
    whatImproved: [
      "Improved local visibility gave the business more opportunities to appear when nearby customers searched for relevant plumbing services.",
      "The profile recorded an upward trend in customer actions such as calls and direction requests during the measurement period.",
      "A more active, better-presented profile gave customers more confidence to make contact.",
    ],
    whyItMattered:
      "Plumbing customers rarely scroll far — they call one of the first businesses that looks nearby and trustworthy. Being visible across more of the service area, with a profile that looks maintained, directly increases the number of chances to win that call.",
    limitations: [
      "Results relate to this business and the areas monitored — they are not a guarantee for any other business.",
      "Competitor activity and Google changes can shift local visibility over time.",
    ],
  },
  {
    slug: "electrical-review-process",
    business: "Australian Electrical Business",
    service: "Reviews and Reputation",
    headline: "Fresher Reviews When Customers Are Comparing Sparkies",
    summary:
      "Review activity had become inconsistent and older reviews were doing all the work, which cost the business trust against better-reviewed competitors.",
    measurementPeriod: "Observed over several months",
    metrics: [
      {
        value: "Improved",
        label: "Local visibility supported by fresher reviews",
        note: "Relates to the tracked search terms monitored for this business only. Recorded over several months.",
      },
      {
        value: "Upward",
        label: "Percentage change in review volume",
        note: "Directional trend only. An exact percentage will be published once verified in the client's reports.",
      },
      {
        value: "More",
        label: "New customer reviews received",
        note: "Recorded during the measurement period; review counts not published without verified client approval.",
      },
    ],
    startingPoint: [
      "Review activity had become inconsistent — requests were usually forgotten once a job was finished.",
      "Reviews were left without a response, so the business looked inattentive to anyone reading them.",
      "Customer feedback was not being used anywhere in the business's marketing.",
    ],
    workCompleted: [
      "Set up a simple, repeatable review request that goes out after completed jobs.",
      "Introduced professional responses to the reviews received.",
      "Gave the owner one place to see review activity as it comes in.",
    ],
    whatImproved: [
      "A clearer review process helped the business collect fresher customer feedback and provide stronger trust signals to people comparing local electricians.",
      "The business began receiving fresh reviews more consistently, giving potential customers stronger and more recent proof when deciding who to contact.",
      "Customers were given a faster, easier way to leave feedback instead of being asked in passing on site.",
    ],
    whyItMattered:
      "When two electricians look equally capable, recent reviews decide who gets the call. A steady flow of fresh feedback gives customers a reason to pick this business over one whose last review is years old.",
    limitations: [
      "Review volume depends on job volume, customer willingness and seasonality.",
      "No review figures are published here without verified approval from the client.",
    ],
  },
  {
    slug: "rubbish-removal-local-visibility",
    business: "Australian Rubbish Removal Business",
    service: "Google Business Profile & Local SEO",
    headline: "Easier to Find and Easier to Contact",
    summary:
      "The business relied on word of mouth, with almost no presence in local search and no clear path for new customers to get in touch.",
    measurementPeriod: "Observed over several months",
    geoGrid: {
      searchCategory: "Rubbish removal services",
      image: "/images/heatmap-gutter-service.png",
      observation:
        "The business began appearing across more of the tracked service area, with more work still to do in the outer suburbs monitored.",
    },
    metrics: [
      {
        value: "Improved",
        label: "Google Maps visibility across the monitored grid",
        note: "Average position movement for the tracked search term and monitored grid only, with outer suburbs still developing.",
      },
      {
        value: "Upward",
        label: "Percentage change in profile actions",
        note: "Directional trend only. An exact percentage will be published once verified in the client's reports.",
      },
      {
        value: "More",
        label: "Calls and quote requests captured",
        note: "Recorded during the measurement period; totals not published without verified client approval.",
      },
    ],
    startingPoint: [
      "The business was difficult to find in local search outside its immediate area.",
      "Service information gave potential customers little idea of what could be removed or how pricing worked.",
      "Enquiries were not tracked, so calls and quote requests were easy to lose.",
    ],
    workCompleted: [
      "Built out the Google profile with proper services, categories and job photos.",
      "Established ongoing profile activity and a review-request process.",
      "Added simple enquiry tracking and follow-up so nothing sits unanswered.",
    ],
    whatImproved: [
      "The improved Google profile and enquiry paths made it easier for local customers to find the business, understand its services and get in touch.",
      "The business appeared across additional parts of the monitored service area, increasing its opportunities to be seen by people searching nearby.",
      "Calls and quote requests were captured in one place, reducing the chance of an enquiry being missed.",
    ],
    whyItMattered:
      "Rubbish removal jobs are usually booked on the spot. Being visible in more areas and easy to contact means fewer customers give up and call the next business on the list.",
    limitations: [
      "Monitored areas represent a selected scan, not an entire metropolitan area.",
      "Demand for this service is seasonal, which affects enquiry volume independently of marketing.",
    ],
  },
  {
    slug: "local-service-lead-follow-up",
    business: "Australian Local Service Business",
    service: "Lead Tracking and Follow-Up",
    headline: "Replying Before the Enquiry Goes Cold",
    summary:
      "Enquiries arrived across calls, forms and messages with no single place to see them, so some were acknowledged late and others not at all.",
    measurementPeriod: "Observed over several months",
    geoGrid: {
      searchCategory: "Local trade services",
      image: "/images/heatmap-landscaper.png",
      observation:
        "Visibility movement was observed across the tracked service area alongside the follow-up work.",
    },
    metrics: [
      {
        value: "Improved",
        label: "Google Maps visibility across the monitored grid",
        note: "Average position movement for the tracked search term and monitored grid only — not an entire city. Recorded over several months.",
      },
      {
        value: "Reduced",
        label: "Percentage change in first-response time",
        note: "Directional trend only. An exact percentage will be published once verified in the client's reports.",
      },
      {
        value: "More",
        label: "Enquiries followed up before going cold",
        note: "Recorded during the measurement period; totals not published without verified client approval.",
      },
    ],
    startingPoint: [
      "New enquiries were not always acknowledged quickly, especially during busy job days.",
      "Missed calls were often never returned.",
      "The owner had no clear tracking of calls and quote requests, or which ones had been followed up.",
    ],
    workCompleted: [
      "Brought calls, forms and messages into one simple enquiry view.",
      "Set up automatic acknowledgements to the customer and instant notifications to the business.",
      "Added missed-call follow-up and reminders so nothing sits unanswered.",
    ],
    whatImproved: [
      "Faster enquiry notifications and follow-up reduced response delays and helped the business contact potential customers before their enquiries went cold.",
      "New enquiries received an immediate acknowledgement, so customers knew they had been heard rather than assuming they were being ignored.",
      "The owner could see every enquiry in one place and who still needed a call back.",
    ],
    whyItMattered:
      "In trade services the business that replies first usually wins the job. Cutting the delay between an enquiry arriving and someone responding reduces the chance of a customer booking with whoever answered sooner.",
    limitations: [
      "Outcomes depend on how consistently the business uses the process day to day.",
      "No enquiry, call or job totals are published here without verified approval from the client.",
    ],
  },
];

export const getCaseStudy = (slug?: string) =>
  caseStudies.find((study) => study.slug === slug);

/** Only genuine GeoGrid examples, capped at four for a balanced 2x2 grid. */
export const geoGridExamples = caseStudies
  .filter((study) => Boolean(study.geoGrid))
  .slice(0, 4);
